import SentryService from '@/services/sentry/Sentry.service';
import { NotificationType } from '@/shared/types/generated';
import { format } from 'date-fns';

/**
 * Processes a deep link URL to extract only the path and query parameters,
 * removing the base URL to ensure internal navigation without page refresh.
 *
 * @param url - The full URL or path
 * @returns The path and query string only (e.g., "/sessions/123?tab=details")
 */
export const processDeepLinkUrl = (url: string): string => {
  try {
    // If it's already a relative path, return as-is
    if (url.startsWith('/')) {
      return url;
    }

    // If it's a full URL, extract the pathname and search
    const urlObj = new URL(url);
    return `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
  } catch (error) {
    SentryService.captureError('Failed to process deep link URL:' + url, error);
    return url; // Return original URL as fallback
  }
};

export const getNotificationTitle = (type: NotificationType): string => {
  switch (type) {
    case NotificationType.ConsultationScheduled:
      return 'Session Confirmed';
    case NotificationType.ConsultationReminder:
      return 'Session Reminder';
    case NotificationType.ConsultationReminder_48Hours:
      return 'Session Coming Up';
    case NotificationType.ConsultationReminderDayOfToClient:
      return 'Session Today';
    case NotificationType.ConsultationReminderDayOfToStylist:
      return 'Session Today';
    case NotificationType.ConsultationMeetingScheduledToClient:
      return "You're all set!";
    case NotificationType.ConsultationMeetingScheduledToStylist:
      return "You've got a booking!";
    case NotificationType.ConsultationCancelled:
      return 'Session Canceled';
    case NotificationType.ConsultationCancelledPaymentIssueToClient:
      return 'Canceled';
    case NotificationType.ConsultationClientNoShowToClient:
      return 'Missed Appointment';
    case NotificationType.ConsultationClientNoShowToStylist:
      return 'Session Update';
    case NotificationType.ConsultationStylistNoShowToClient:
      return 'Stylist No Show';
    case NotificationType.ConsultationStylistNoShowToStylist:
      return 'Missed Session';
    case NotificationType.ConsultationStylistNoShowToOwner:
      return 'Session Update';
    case NotificationType.InstantStylingRequestMatch:
      return 'Match Found';
    case NotificationType.InstantStylingMatchFoundToStylist:
      return 'Match Found';
    case NotificationType.InstantStylingToggleOff:
      return 'Instant Styling Turned Off';
    case NotificationType.NewMessage:
      return 'New Message';
    case NotificationType.BookingRequestToStylist:
      return 'New Booking Request';
    case NotificationType.InPersonRequestToStylist:
      return 'New In-Person Request';
    case NotificationType.InPersonRequestApprovedToClient:
      return 'Request Approved';
    case NotificationType.InPersonRequestDeclinedToClient:
      return 'Request Declined';
    case NotificationType.InPersonRequestExpiredToClient:
    case NotificationType.InPersonRequestExpiredToStylist:
      return 'Request Expired';
    default:
      return 'Notification';
  }
};

export const getNotificationDescription = (
  type: NotificationType,
  variables?: Record<string, unknown> | null,
): string => {
  const {
    other_user_name,
    consultation_start_time,
    client_name,
    stylist_name,
  } = variables || {};

  switch (type) {
    case NotificationType.ConsultationScheduled:
      return `Your session with ${other_user_name} has been confirmed`;
    case NotificationType.ConsultationReminder:
      return `Don't forget about your session with ${other_user_name}`;
    case NotificationType.ConsultationReminder_48Hours:
      return `Heads up! You have a session upcoming with ${other_user_name} at ${consultation_start_time}.`;
    case NotificationType.ConsultationReminderDayOfToClient:
      return `Today's the day! Your session with ${other_user_name} is at ${consultation_start_time}.`;
    case NotificationType.ConsultationReminderDayOfToStylist:
      return `Heads up! You have a session today with ${other_user_name} at ${consultation_start_time}.`;
    case NotificationType.ConsultationMeetingScheduledToClient:
      return `You're all set! You've scheduled a session with ${other_user_name}.`;
    case NotificationType.ConsultationMeetingScheduledToStylist:
      return `${other_user_name} has scheduled a session with you.`;
    case NotificationType.ConsultationCancelled:
      return `Change of plans - Your session on ${consultation_start_time} with ${other_user_name} was canceled.`;
    case NotificationType.ConsultationCancelledPaymentIssueToClient:
      return `Your session with ${other_user_name} at ${consultation_start_time} was canceled due to a payment issue.`;
    case NotificationType.ConsultationClientNoShowToClient:
      return `Psst...looks like you missed your session with ${other_user_name}. A no-show fee will be charged.`;
    case NotificationType.ConsultationClientNoShowToStylist:
      return `${other_user_name} didn't make it to ${consultation_start_time} session. They will be charged a no-show fee.`;
    case NotificationType.ConsultationStylistNoShowToClient:
      return `Your stylist wasn't able to make it.`;
    case NotificationType.ConsultationStylistNoShowToStylist:
      return `Looks like you missed your session with ${other_user_name}. Things happen!`;
    case NotificationType.ConsultationStylistNoShowToOwner:
      return `${other_user_name} missed their session`;
    case NotificationType.InstantStylingRequestMatch:
      return `You've been matched with ${other_user_name}`;
    case NotificationType.InstantStylingMatchFoundToStylist:
      return `A client needs you — right now! You have 1 minute to accept before the request moves to the next stylist.`;
    case NotificationType.InstantStylingToggleOff:
      return `Psst... Your Instant Styling availability was turned off due to inactivity. Toggle it back on when you're ready.`;
    case NotificationType.NewMessage:
      return `You have received a new message! Sign into your account to see what they said!`;
    case NotificationType.BookingRequestToStylist:
      return `${client_name} would like to book you. Tap to accept or suggest a new time.`;
    case NotificationType.InPersonRequestToStylist:
      return `${client_name} has requested an in-person session on ${consultation_start_time}. Tap to review.`;
    case NotificationType.InPersonRequestApprovedToClient:
      return `Great news! ${stylist_name} has approved your in-person session request.`;
    case NotificationType.InPersonRequestDeclinedToClient:
      return `${stylist_name} has declined your in-person session request.`;
    case NotificationType.InPersonRequestExpiredToClient:
      return `Your in-person session request with ${stylist_name} for ${consultation_start_time} has expired.`;
    case NotificationType.InPersonRequestExpiredToStylist:
      return `The in-person session request from ${client_name} for ${consultation_start_time} has expired.`;
    default:
      return 'You have a new notification';
  }
};

export const formatTimestamp = (date: Date): string => {
  const now = new Date();

  // Get start of today for proper calendar day comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const notificationDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  // Calculate difference in calendar days
  const diffInMs = today.getTime() - notificationDate.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    // Same calendar day - show time
    return format(date, 'h:mm a');
  } else if (diffInDays === 1) {
    // Yesterday
    return 'Yesterday';
  } else if (diffInDays >= 2 && diffInDays <= 6) {
    // 2-6 days ago - show day of week (but not if it's the same day of week as today)
    const todayDayOfWeek = now.getDay();
    const notificationDayOfWeek = date.getDay();

    // If it's the same day of the week as today, show "X days ago" instead
    if (todayDayOfWeek === notificationDayOfWeek) {
      return `${diffInDays} days ago`;
    } else {
      return format(date, 'EEEE');
    }
  } else {
    // 7+ days ago - show relative time
    if (diffInDays < 30) {
      return `${diffInDays} days ago`;
    } else if (diffInDays < 365) {
      const diffInMonths = Math.floor(diffInDays / 30);
      return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    } else {
      const diffInYears = Math.floor(diffInDays / 365);
      return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
    }
  }
};
